import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  FileText,
  Loader2,
  Upload,
  X,
} from "lucide-react";
import { Button, Dialog, Select } from "@/components/ui";
import { Switch } from "@/components/ui/Switch";
import { trpc } from "@/lib/trpc";
import { useAppStore } from "@/stores/app.store";

type Slot = "articles" | "prices" | "rabatt" | "zip" | "ignore";
const SLOT_LABEL: Record<Slot, string> = {
  articles: "Änderungen / Artikelstamm (Datanorm.001)",
  zip: "ZIP aus der Impex-Mail (datanorm.zip)",
  prices: "Nettopreise (Datpreis.001)",
  rabatt: "Rabattliste (Datanorm.rab)",
  ignore: "ignorieren",
};

/** Dateityp anhand des Namens erraten (Impex-Namensschema). */
function guessSlot(name: string): Slot {
  const n = name.toLowerCase();
  if (n.endsWith(".zip")) return "zip";
  if (n.endsWith(".rab")) return "rabatt";
  if (/preis.*\.\d{3}$/.test(n)) return "prices";
  if (/^datanorm\.\d{3}$/.test(n)) return "articles";
  if (n.endsWith(".wrg")) return "ignore";
  return "ignore";
}

const fmtMB = (b: number) =>
  b >= 1024 * 1024
    ? `${(b / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(b / 1024))} KB`;
const fmtN = (n: number) => n.toLocaleString("de-AT");
const eur = (c: number | null | undefined) =>
  c == null
    ? "–"
    : (c / 100).toLocaleString("de-AT", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }) + " €";

type PickedFile = { file: File; slot: Slot };

function uploadFile(
  url: string,
  file: File,
  onProgress: (loaded: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    xhr.withCredentials = true;
    xhr.setRequestHeader("Content-Type", "application/octet-stream");
    xhr.upload.onprogress = (e) => onProgress(e.loaded);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) return resolve();
      let msg = `Upload fehlgeschlagen (Fehler ${xhr.status}).`;
      try {
        const j = JSON.parse(xhr.responseText);
        if (j?.error) msg = j.error;
      } catch {
        if (xhr.status === 413)
          msg =
            "Die Datei ist für den Server zu groß (413). Bitte Support kontaktieren.";
      }
      reject(new Error(msg));
    };
    xhr.onerror = () =>
      reject(
        new Error(
          "Die Verbindung wurde unterbrochen. Bitte Internetverbindung prüfen und erneut versuchen.",
        ),
      );
    xhr.ontimeout = () => reject(new Error("Zeitüberschreitung beim Upload."));
    xhr.send(file);
  });
}

type Stand = { kind: "full" | "delta"; date: string | null; text: string };
type Report = {
  /** delta = Änderungsdatei, full = kompletter Artikelstamm */
  mode?: "delta" | "full";
  file: {
    articles: number;
    rows: number;
    kinds: Record<string, number>;
    nettopreise: number;
    rabattgruppen: number;
    names: string[];
    stand?: Stand;
  };
  previousStand?: (Stand & { approx?: boolean }) | null;
  recalc?: { override: number; group: number; groupGone: number } | null;
  existingPriceRows: number;
  matchRate: number;
  counts: Record<string, number>;
  samples: {
    price: {
      num: string;
      name: string;
      oldList: number | null;
      newList: number | null;
      oldNet: number | null;
      newNet: number | null;
    }[];
    newArticles: { num: string; name: string; list: number | null }[];
    notInFile: { num: string; name: string }[];
    renamed: { num: string; oldName: string; name: string }[];
  };
  warnings: string[];
  result?: Record<string, number>;
};

export function CatalogUpdateDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const utils = trpc.useUtils();
  const addToast = useAppStore((s) => s.addToast);
  const suppliers = trpc.supplier.list.useQuery(undefined, { enabled: open });
  const current = trpc.catalogImport.current.useQuery(undefined, {
    enabled: open,
    refetchOnMount: "always",
  });

  const [supplierId, setSupplierId] = useState("");
  const [picked, setPicked] = useState<PickedFile[]>([]);
  const [updateSalePrices, setUpdateSalePrices] = useState(true);
  const [updateNames, setUpdateNames] = useState(true);
  const [jobId, setJobId] = useState<string | null>(null);
  const [uploadState, setUploadState] = useState<{
    active: boolean;
    label: string;
    done: number;
    total: number;
    error?: string;
  } | null>(null);
  const uploadingRef = useRef(false);
  // verworfene/abgeschlossene Läufe nicht automatisch wieder aufgreifen
  const dismissed = useRef<Set<string>>(new Set());
  const fileInput = useRef<HTMLInputElement>(null);

  // Beim Öffnen: läuft/wartet noch ein Lauf? → dort weitermachen
  useEffect(() => {
    if (!open) return;
    const j = current.data;
    if (j && !jobId && !dismissed.current.has(j.id)) {
      const recentlyFinished =
        j.finishedAt &&
        Date.now() - new Date(j.finishedAt).getTime() < 30 * 60 * 1000;
      if (
        ["uploading", "analyzing", "analyzed", "applying"].includes(j.status) ||
        (["done", "failed"].includes(j.status) && recentlyFinished)
      ) {
        setJobId(j.id);
        if (j.options) {
          setUpdateSalePrices(j.options.updateSalePrices);
          setUpdateNames(j.options.updateNames);
        }
      }
    }
  }, [open, current.data, jobId]);

  useEffect(() => {
    if (!open) return;
    if (!supplierId && suppliers.data?.length) {
      const impex = suppliers.data.find((s) => /impex/i.test(s.name));
      setSupplierId((impex ?? suppliers.data[0])!.id);
    }
  }, [open, suppliers.data, supplierId]);

  const status = trpc.catalogImport.status.useQuery(
    { id: jobId ?? "00000000-0000-4000-8000-000000000000" },
    {
      enabled: open && !!jobId,
      refetchInterval: (q) => {
        const st = q.state.data?.status;
        return st === "analyzing" || st === "applying" ? 1500 : false;
      },
      refetchOnMount: "always",
      // auch im Hintergrund-Tab weiter abfragen (Upload/Prüfung dauern, man wechselt gern kurz den Tab)
      refetchIntervalInBackground: true,
    },
  );
  const job = jobId ? status.data : null;
  const report = (job?.report ?? null) as Report | null;

  const reset = () => {
    if (jobId) dismissed.current.add(jobId);
    setJobId(null);
    setPicked([]);
    setUploadState(null);
    utils.catalogImport.current.invalidate();
  };

  const create = trpc.catalogImport.create.useMutation();
  const analyze = trpc.catalogImport.analyze.useMutation();
  const apply = trpc.catalogImport.execute.useMutation({
    onSuccess: () => status.refetch(),
  });
  const cancel = trpc.catalogImport.cancel.useMutation({
    onSuccess: () => reset(),
  });

  // fertig → Artikelliste aktualisieren
  useEffect(() => {
    if (job?.status === "done") {
      utils.article.list.invalidate();
      utils.article.imports.invalidate();
    }
  }, [job?.status, utils]);

  const hasArticles = picked.some(
    (p) => p.slot === "articles" || p.slot === "zip",
  );
  const hasPrices = picked.some((p) => p.slot === "prices");
  const hasRabatt = picked.some((p) => p.slot === "rabatt");
  // Ein ZIP (z. B. die komplette Impex-Mail-Anlage) darf Nettopreise und Rabattliste schon enthalten — die Prüfung meldet es, falls nicht.
  const hasZip = picked.some((p) => p.slot === "zip");
  const canStart =
    !!supplierId && hasArticles && ((hasPrices && hasRabatt) || hasZip);

  const onFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (!list.length) return;
    setPicked((cur) => {
      const next = [...cur];
      for (const f of list) {
        const slot = guessSlot(f.name);
        if (next.some((p) => p.file.name === f.name && p.file.size === f.size))
          continue;
        next.push({ file: f, slot });
      }
      return next;
    });
  };

  const start = async () => {
    if (!canStart || uploadingRef.current) return;
    uploadingRef.current = true;
    try {
      const { id } = await create.mutateAsync({ supplierId });
      setJobId(id);
      const files = picked.filter((p) => p.slot !== "ignore");
      const totalBytes = files.reduce((a, p) => a + p.file.size, 0);
      let doneBytes = 0;
      setUploadState({ active: true, label: "", done: 0, total: totalBytes });
      let firstArticle = true;
      for (const p of files) {
        const replace = p.slot === "articles" && firstArticle ? "1" : "0";
        if (p.slot === "articles") firstArticle = false;
        const base = doneBytes;
        setUploadState({
          active: true,
          label: `${p.file.name} wird hochgeladen …`,
          done: base,
          total: totalBytes,
        });
        await uploadFile(
          `/upload/catalog/${id}/${p.slot}?name=${encodeURIComponent(p.file.name)}&replace=${replace}`,
          p.file,
          (loaded) =>
            setUploadState({
              active: true,
              label: `${p.file.name} wird hochgeladen …`,
              done: base + loaded,
              total: totalBytes,
            }),
        );
        doneBytes += p.file.size;
      }
      setUploadState(null);
      await analyze.mutateAsync({ id, updateSalePrices, updateNames });
      status.refetch();
    } catch (e) {
      setUploadState({
        active: false,
        label: "",
        done: 0,
        total: 0,
        error: e instanceof Error ? e.message : "Upload fehlgeschlagen.",
      });
    } finally {
      uploadingRef.current = false;
    }
  };

  const close = (v: boolean) => {
    if (!v && uploadState?.active) {
      addToast({
        type: "warning",
        title: "Upload läuft noch",
        description: "Bitte das Fenster bis zum Ende des Uploads offen lassen.",
      });
      return;
    }
    onOpenChange(v);
  };

  const pct = (done: number, total: number) =>
    total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;

  const c = report?.counts;
  const isDelta = report?.mode === "delta";
  const view = useMemo(() => {
    if (uploadState && (uploadState.active || uploadState.error))
      return "upload" as const;
    if (!job) return "select" as const;
    if (job.status === "uploading") return "select" as const;
    return job.status as
      "analyzing" | "analyzed" | "applying" | "done" | "failed" | "cancelled";
  }, [job, uploadState]);

  return (
    <Dialog
      open={open}
      onOpenChange={close}
      title="Katalog aktualisieren (Datanorm)"
      wide
      guardClose={false}
    >
      <div className="space-y-4 text-sm">
        {/* ── 1. Dateien auswählen ───────────────────────────────────────────── */}
        {(view === "select" || view === "cancelled") && (
          <>
            <p className="text-text-secondary">
              Spielt eine Datanorm-Lieferung ein — entweder die{" "}
              <strong>Änderungen</strong> (der Normalfall) oder einen{" "}
              <strong>kompletten Artikelstamm</strong>:{" "}
              <strong>neue Preise</strong>, <strong>neue Artikel</strong> und
              geänderte Bezeichnungen werden übernommen. Es wird{" "}
              <strong>nichts gelöscht</strong>. Vor dem Einspielen siehst du
              eine Vorschau mit allen Änderungen.
            </p>
            <Select
              label="Lieferant"
              value={supplierId}
              onChange={(e) => setSupplierId(e.target.value)}
              options={[
                { value: "", label: "— wählen —" },
                ...(suppliers.data ?? []).map((s) => ({
                  value: s.id,
                  label: s.name,
                })),
              ]}
            />

            <div>
              <p className="label">Dateien</p>
              <p className="text-xs text-text-tertiary mb-2">
                Am einfachsten die <strong>ZIP-Datei aus der Impex-Mail</strong>{" "}
                unverändert auswählen (sie enthält Änderungen, Nettopreise und
                Rabattliste). Oder alle Dateien gleichzeitig: <em>Datanorm.001</em>{" "}
                (bzw. <em>datanorm.zip</em>), <em>Datpreis.001</em> und{" "}
                <em>Datanorm.rab</em>. Das Programm erkennt selbst, ob es
                Änderungen oder ein kompletter Artikelstamm sind.
              </p>
              <input
                ref={fileInput}
                type="file"
                multiple
                className="hidden"
                onChange={onFiles}
              />
              <Button
                variant="secondary"
                onClick={() => fileInput.current?.click()}
              >
                <Upload className="w-4 h-4" /> Dateien auswählen
              </Button>

              {picked.length > 0 && (
                <ul className="mt-3 divide-y divide-border-subtle border border-border-subtle rounded-md">
                  {picked.map((p, i) => (
                    <li key={i} className="flex items-center gap-3 px-3 py-2">
                      <FileText className="w-4 h-4 text-text-tertiary shrink-0" />
                      <span className="flex-1 min-w-0 truncate">
                        {p.file.name}{" "}
                        <span className="text-text-tertiary text-xs">
                          {fmtMB(p.file.size)}
                        </span>
                      </span>
                      <select
                        className="input !w-auto text-xs"
                        value={p.slot}
                        onChange={(e) =>
                          setPicked((cur) =>
                            cur.map((x, j) =>
                              j === i
                                ? { ...x, slot: e.target.value as Slot }
                                : x,
                            ),
                          )
                        }
                      >
                        {(Object.keys(SLOT_LABEL) as Slot[]).map((s) => (
                          <option key={s} value={s}>
                            {SLOT_LABEL[s]}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="text-text-tertiary hover:text-danger"
                        onClick={() =>
                          setPicked((cur) => cur.filter((_, j) => j !== i))
                        }
                        title="Entfernen"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {picked.length > 0 && !canStart && !hasZip && (
                <p className="mt-2 text-xs text-warning flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Es fehlt noch:{" "}
                  {[
                    !hasArticles && "Änderungen bzw. Artikelstamm (Datanorm.001)",
                    !hasPrices && "Nettopreise (Datpreis.001)",
                    !hasRabatt && "Rabattliste (Datanorm.rab)",
                  ]
                    .filter(Boolean)
                    .join(", ")}
                  . Nettopreise und Rabattliste sind nötig, damit keine
                  Einkaufspreise verloren gehen.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className="flex items-start gap-3">
                <Switch
                  checked={updateSalePrices}
                  onChange={setUpdateSalePrices}
                />
                <span>
                  <span className="text-text-primary">
                    Verkaufspreise mitziehen
                  </span>
                  <span className="block text-xs text-text-tertiary">
                    Nur wenn der Verkaufspreis bisher dem alten Listenpreis
                    entsprach. Von Hand angepasste Verkaufspreise bleiben immer
                    unverändert.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-3">
                <Switch checked={updateNames} onChange={setUpdateNames} />
                <span>
                  <span className="text-text-primary">
                    Geänderte Bezeichnungen übernehmen
                  </span>
                  <span className="block text-xs text-text-tertiary">
                    Aus: bestehende Artikelbezeichnungen bleiben, auch wenn sie
                    sich im Katalog ändern.
                  </span>
                </span>
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => close(false)}>
                Schließen
              </Button>
              <Button
                variant="primary"
                disabled={!canStart}
                loading={create.isPending}
                onClick={start}
              >
                Hochladen &amp; prüfen
              </Button>
            </div>
          </>
        )}

        {/* ── 2. Upload ──────────────────────────────────────────────────────── */}
        {view === "upload" && uploadState && (
          <div className="space-y-3">
            {uploadState.error ? (
              <>
                <div className="rounded-md border border-danger/40 bg-danger/5 p-3 text-danger flex gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{uploadState.error}</span>
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      if (jobId) cancel.mutate({ id: jobId });
                      else reset();
                    }}
                  >
                    Zurück
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="flex items-center gap-2 text-text-primary">
                  <Loader2 className="w-4 h-4 animate-spin" />{" "}
                  {uploadState.label}
                </p>
                <ProgressBar value={pct(uploadState.done, uploadState.total)} />
                <p className="text-xs text-text-tertiary">
                  {fmtMB(uploadState.done)} von {fmtMB(uploadState.total)} —
                  bitte dieses Fenster offen lassen, bis der Upload fertig ist.
                </p>
              </>
            )}
          </div>
        )}

        {/* ── 3. Prüfung läuft / Einspielen läuft ────────────────────────────── */}
        {(view === "analyzing" || view === "applying") && job && (
          <div className="space-y-3">
            <div className="flex items-baseline justify-between gap-3">
              <p className="flex items-center gap-2 text-text-primary">
                <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                {job.phase ??
                  (view === "analyzing"
                    ? "Wird geprüft …"
                    : "Wird eingespielt …")}
              </p>
              <span className="text-2xl font-semibold text-accent tabular-nums">
                {pct(job.progressDone, job.progressTotal)} %
              </span>
            </div>
            <ProgressBar value={pct(job.progressDone, job.progressTotal)} />
            <p className="text-xs text-text-tertiary">
              {view === "analyzing"
                ? "Die Prüfung ändert noch nichts an deinen Daten (dauert ca. 1–2 Minuten). "
                : "Das CRM kann währenddessen normal weiterverwendet werden (dauert ca. 5–10 Minuten). "}
              Du kannst dieses Fenster schließen — der Lauf geht im Hintergrund
              weiter und du findest ihn beim erneuten Öffnen wieder.
            </p>
          </div>
        )}

        {/* ── 4. Vorschau ────────────────────────────────────────────────────── */}
        {view === "analyzed" && report && c && (
          <>
            <div className="rounded-md border border-border-subtle p-3 bg-bg-subtle">
              <p className="text-text-primary font-medium">
                Vorschau für {job?.supplierName ?? "Lieferant"} — noch wurde
                nichts verändert
              </p>
              {isDelta ? (
                <>
                  <p className="text-xs text-text-tertiary mt-0.5">
                    <strong>Änderungslieferung</strong>
                    {report.file.stand?.date
                      ? ` (${report.file.stand.text})`
                      : ""}
                    {report.previousStand
                      ? ` — baut auf dem bisherigen Stand „${report.previousStand.text}${report.previousStand.approx && report.previousStand.date ? ` (${report.previousStand.date.split("-").reverse().join(".")})` : ""}" auf.`
                      : "."}
                  </p>
                  <p className="text-xs text-text-tertiary mt-0.5">
                    {fmtN(c.total ?? 0)} neue bzw. geänderte Artikel in der
                    Datei, davon {fmtN(c.matched ?? 0)} bereits vorhanden.
                    {report.recalc &&
                    report.recalc.override + report.recalc.group > 0
                      ? ` Zusätzlich ändert sich der Einkaufspreis von ${fmtN(report.recalc.override + report.recalc.group)} Artikeln, die nicht in der Datei stehen, durch die neue Rabattliste bzw. die neuen Nettopreise.`
                      : ""}
                  </p>
                </>
              ) : (
                <p className="text-xs text-text-tertiary mt-0.5">
                  {fmtN(c.total ?? 0)} Artikel in der Datei, davon{" "}
                  {fmtN(c.matched ?? 0)} bereits vorhanden (
                  {(report.matchRate * 100).toFixed(1)} % Übereinstimmung).
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Stat label="Neue Artikel" value={c.newArticles} tone="success" />
              <Stat
                label="Preise geändert"
                value={c.priceChanged}
                tone="accent"
              />
              <Stat label="Unverändert" value={c.unchanged} />
              {isDelta ? (
                <Stat
                  label="Löschhinweise"
                  value={c.deleteFlagged}
                  tone={c.deleteFlagged ? "warning" : undefined}
                  hint="Artikel bleiben erhalten"
                />
              ) : (
                <Stat
                  label="Nicht mehr in Datei"
                  value={c.notInFile}
                  tone={c.notInFile ? "warning" : undefined}
                  hint="bleiben erhalten"
                />
              )}
            </div>

            <ul className="space-y-1 text-text-secondary">
              <li>
                <strong>Listenpreise:</strong> {fmtN(c.listUp ?? 0)} teurer,{" "}
                {fmtN(c.listDown ?? 0)} günstiger.{" "}
                <strong>Einkaufspreise:</strong> {fmtN(c.netUp ?? 0)} teurer,{" "}
                {fmtN(c.netDown ?? 0)} günstiger
                {c.netGained ? `, ${fmtN(c.netGained)} neu` : ""}
                {c.netLost ? `, bei ${fmtN(c.netLost)} weggefallen` : ""}.
              </li>
              <li>
                <strong>Verkaufspreise:</strong>{" "}
                {updateSalePrices
                  ? `${fmtN(c.saleFollow ?? 0)} werden mitgezogen${c.saleKept ? `, ${fmtN(c.saleKept)} von Hand angepasste bleiben` : ""}`
                  : "bleiben alle unverändert"}
                .
              </li>
              <li>
                <strong>Artikeldaten:</strong>{" "}
                {updateNames
                  ? `${fmtN(c.nameChanged ?? 0)} Bezeichnungen, `
                  : ""}
                {fmtN(c.unitChanged ?? 0)} Einheiten,{" "}
                {fmtN(c.categoryChanged ?? 0)} Warengruppen,{" "}
                {fmtN(c.eanChanged ?? 0)} EANs ändern sich.
              </li>
              {c.priceNew ? (
                <li>
                  {fmtN(c.priceNew)} vorhandene Artikel bekommen zusätzlich eine
                  Preiszeile dieses Lieferanten.
                </li>
              ) : null}
              {c.skippedDeleted ? (
                <li>
                  {fmtN(c.skippedDeleted)} von dir gelöschte Artikel bleiben
                  gelöscht.
                </li>
              ) : null}
              {c.deleteFlagged ? (
                <li>
                  {fmtN(c.deleteFlagged)} Löschhinweise (L/X) des Lieferanten,
                  davon {fmtN(c.deleteInDb ?? 0)} im Bestand — diese Artikel
                  werden <strong>nicht</strong> gelöscht.
                </li>
              ) : null}
              {isDelta && (
                <li className="text-xs text-text-tertiary">
                  Hinweis: Reine EAN-Änderungen und Artikel, deren eigener
                  Nettopreis weggefallen ist, kommen erst mit der nächsten
                  Gesamtlieferung („Artikelstamm per …"). Alles andere ist mit
                  dieser Änderungslieferung vollständig aktuell.
                </li>
              )}
              <li className="text-xs text-text-tertiary">
                Einkaufspreis-Herkunft: {fmtN(c.netOverride ?? 0)} eigene
                Nettopreise, {fmtN(c.netGroup ?? 0)} über Rabattgruppe
                {c.groupMissing
                  ? `, ${fmtN(c.groupMissing)} Artikel mit Rabattgruppe ohne Eintrag in der Rabattliste (ohne Einkaufspreis)`
                  : ""}
                .
              </li>
            </ul>

            {(c.netLost ?? 0) > 0 && (
              <p className="text-xs text-warning flex gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                Bei {fmtN(c.netLost ?? 0)} Artikeln fällt der bisherige
                Einkaufspreis weg (keine Rabattgruppe/kein Nettopreis mehr in
                den neuen Dateien). Falls das nicht erwartet ist: abbrechen und
                die Dateien prüfen.
              </p>
            )}

            {report.samples.price.length > 0 && (
              <SampleTable
                title="Größte Preisänderungen (Einkauf)"
                head={["Artikel", "Liste alt → neu", "Einkauf alt → neu"]}
                rows={report.samples.price.map((s) => [
                  `${s.num} · ${s.name}`,
                  `${eur(s.oldList)} → ${eur(s.newList)}`,
                  `${eur(s.oldNet)} → ${eur(s.newNet)}`,
                ])}
              />
            )}
            {report.samples.newArticles.length > 0 && (
              <SampleTable
                title="Beispiele neuer Artikel"
                head={["Artikel", "Listenpreis"]}
                rows={report.samples.newArticles.map((s) => [
                  `${s.num} · ${s.name}`,
                  eur(s.list),
                ])}
              />
            )}
            {report.samples.notInFile.length > 0 && (
              <SampleTable
                title="Beispiele: nicht mehr in der Datei (bleiben im CRM erhalten)"
                head={["Artikel"]}
                rows={report.samples.notInFile.map((s) => [
                  `${s.num} · ${s.name}`,
                ])}
              />
            )}

            <div className="space-y-2 border-t border-border-subtle pt-3">
              <label className="flex items-start gap-3">
                <Switch
                  checked={updateSalePrices}
                  onChange={setUpdateSalePrices}
                />
                <span className="text-text-primary">
                  Verkaufspreise mitziehen
                </span>
              </label>
              <label className="flex items-start gap-3">
                <Switch checked={updateNames} onChange={setUpdateNames} />
                <span className="text-text-primary">
                  Geänderte Bezeichnungen übernehmen
                </span>
              </label>
            </div>

            <div className="flex justify-between gap-2 pt-1">
              <Button
                variant="ghost"
                onClick={() => jobId && cancel.mutate({ id: jobId })}
                loading={cancel.isPending}
              >
                Verwerfen
              </Button>
              <Button
                variant="primary"
                loading={apply.isPending}
                onClick={() =>
                  jobId &&
                  apply.mutate(
                    { id: jobId, updateSalePrices, updateNames },
                    {
                      onError: (e) =>
                        addToast({
                          type: "error",
                          title: "Einspielen nicht möglich",
                          description: e.message,
                        }),
                    },
                  )
                }
              >
                Jetzt einspielen
              </Button>
            </div>
          </>
        )}

        {/* ── 5. Fertig ──────────────────────────────────────────────────────── */}
        {view === "done" && report?.result && (
          <>
            <div className="rounded-md border border-success/40 bg-success/5 p-3 flex gap-2 text-text-primary">
              <CheckCircle2 className="w-4 h-4 text-success mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">
                  Katalog aktualisiert
                  {isDelta ? " (Änderungen)" : ""} ({report.result.seconds}{" "}
                  Sekunden)
                </p>
                <p className="text-text-secondary mt-1">
                  {fmtN(report.result.newArticles ?? 0)} neue Artikel ·{" "}
                  {fmtN(report.result.priceChanged ?? 0)} Preiszeilen geändert ·{" "}
                  {fmtN(report.result.masterUpdated ?? 0)} Artikel aktualisiert
                  · {fmtN(report.result.saleUpdated ?? 0)} Verkaufspreise
                  angepasst
                  {report.result.priceNew
                    ? ` · ${fmtN(report.result.priceNew)} Preiszeilen ergänzt`
                    : ""}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={reset}>
                Weiteren Katalog einspielen
              </Button>
              <Button variant="primary" onClick={() => onOpenChange(false)}>
                Schließen
              </Button>
            </div>
          </>
        )}

        {/* ── Fehler ─────────────────────────────────────────────────────────── */}
        {view === "failed" && job && (
          <>
            <div className="rounded-md border border-danger/40 bg-danger/5 p-3 text-danger flex gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{job.error ?? "Der Lauf ist fehlgeschlagen."}</span>
            </div>
            <div className="flex justify-end gap-2">
              {report && (
                <Button
                  variant="secondary"
                  loading={apply.isPending}
                  onClick={() =>
                    jobId &&
                    apply.mutate(
                      { id: jobId, updateSalePrices, updateNames },
                      {
                        onError: (e) =>
                          addToast({
                            type: "error",
                            title: "Fehler",
                            description: e.message,
                          }),
                      },
                    )
                  }
                >
                  Erneut einspielen
                </Button>
              )}
              <Button
                variant="primary"
                onClick={() => (jobId ? cancel.mutate({ id: jobId }) : reset())}
              >
                Neu starten
              </Button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}

function ProgressBar({ value }: { value?: number }) {
  return (
    <div className="h-2 rounded-full bg-bg-subtle overflow-hidden border border-border-subtle">
      {value == null ? (
        <div className="h-full w-1/3 bg-accent/70 animate-pulse rounded-full" />
      ) : (
        <div
          className="h-full bg-accent transition-all duration-300"
          style={{ width: `${value}%` }}
        />
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
  hint,
}: {
  label: string;
  value?: number;
  tone?: "success" | "accent" | "warning";
  hint?: string;
}) {
  const color =
    tone === "success"
      ? "text-success"
      : tone === "accent"
        ? "text-accent"
        : tone === "warning"
          ? "text-warning"
          : "text-text-primary";
  return (
    <div className="rounded-md border border-border-subtle p-2.5">
      <div className={`text-lg font-semibold ${color}`}>{fmtN(value ?? 0)}</div>
      <div className="text-xs text-text-tertiary">{label}</div>
      {hint && <div className="text-2xs text-text-tertiary">{hint}</div>}
    </div>
  );
}

function SampleTable({
  title,
  head,
  rows,
}: {
  title: string;
  head: string[];
  rows: string[][];
}) {
  return (
    <div>
      <p className="text-xs font-medium text-text-secondary mb-1">{title}</p>
      <div className="overflow-x-auto border border-border-subtle rounded-md">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-text-tertiary text-left">
              {head.map((h) => (
                <th key={h} className="px-2 py-1 font-medium">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-border-subtle">
                {r.map((cell, j) => (
                  <td
                    key={j}
                    className="px-2 py-1 whitespace-nowrap max-w-[420px] truncate"
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
